import {
  createColumnHelper,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { Suspense } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { DataTable } from '@/components/ui/data-table';
import { nutrients } from '@/lib/consts';
import { IngredientNutrient } from '@/types';

import { useIngredientNutrients } from '../api/ingredients-queries';

const columnHelper = createColumnHelper<IngredientNutrient>();

const columns = [
  columnHelper.accessor('created_at', {
    header: 'Date',
    cell: (info) => format(new Date(info.getValue()), 'MMM do, yyyy'),
    size: 30,
  }),
  columnHelper.accessor(
    (ingredientNutrient) => {
      const allergenName = nutrients
        .find((nutrient) => nutrient.key === ingredientNutrient.nutrient)
        ?.label.toLowerCase();
      // TODO: Improve this to
      // a. Merge updates that occured at the same time
      // b. Mark if the allergen was removed from allergens or may contains
      return `Updated ${allergenName} to ${ingredientNutrient.value}`;
    },
    {
      id: 'description',
      header: 'Description',
      cell: (info) => info.getValue(),
      size: 70,
      enableGlobalFilter: false,
    },
  ),
];

interface IngredientNutritionHistoryTablePresentationProps {
  nutrientHistory: IngredientNutrient[];
}

function IngredientNutritionHistoryTablePresentation({
  nutrientHistory,
}: IngredientNutritionHistoryTablePresentationProps) {
  const table = useReactTable({
    data: nutrientHistory,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div className="w-full">
      <DataTable table={table} containerClassName="max-h-80" />
    </div>
  );
}

interface IngredientNutritionHistoryTableProps {
  ingredientId: string;
}

function IngredientNutritionHistoryTable({
  ingredientId,
}: IngredientNutritionHistoryTableProps) {
  const { data: nutrientHistory } = useIngredientNutrients(ingredientId);

  return (
    <IngredientNutritionHistoryTablePresentation
      nutrientHistory={nutrientHistory}
    />
  );
}

interface IngredientNutritionHistoryProps {
  ingredientId: string;
}

export function IngredientNutritionHistory({
  ingredientId,
}: IngredientNutritionHistoryProps) {
  const table = useReactTable({
    data: [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <Accordion type="single" collapsible className="w-full">
      <AccordionItem
        value="item-1"
        className="border-2 border-solid mt-6 rounded-md border-slate-200"
      >
        <AccordionTrigger className="bg-slate-200 px-4">
          Nutrition History
        </AccordionTrigger>
        <AccordionContent className="flex flex-col gap-4 text-balance px-4 pt-4">
          <Suspense
            fallback={
              <DataTable
                table={table}
                containerClassName="max-h-80"
                loading
                skeletonRows={3}
              />
            }
          >
            <IngredientNutritionHistoryTable ingredientId={ingredientId} />
          </Suspense>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
